/**
 * /accounts/:aid/activity — day-by-day list of button presses, with
 * caregiver-configurable inactivity alerts.
 */

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { formatInTimeZone } from 'date-fns-tz';

import { useAccount } from '@/hooks/useAccount';
import { useDevices } from '@/hooks/useDevices';
import { useMyRole } from '@/hooks/useMyRole';
import { useButtonPresses } from '@/hooks/useButtonPresses';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { Spinner } from '@/components/ui/Spinner';
import { ActivityTimeline } from '@/components/activity/ActivityTimeline';
import { ButtonPressList } from '@/components/activity/ButtonPressList';
import { AlertsCard } from '@/components/activity/AlertsCard';
import { LoadErrorBanner } from '@/components/LoadErrorBanner';

const DEFAULT_TZ = 'Europe/London';

export function ActivityPage() {
  const { aid } = useParams<{ aid: string }>();
  const { account, loading: accountLoading } = useAccount(aid ?? null);
  const role = useMyRole(aid ?? null);
  const tz = account?.timeZone || DEFAULT_TZ;

  // Default to "today in the account's timezone." On first render the
  // account doc isn't loaded yet so `tz` is the default; once it
  // arrives, re-seed *if the user hasn't picked a date yet* so a
  // non-UK account doesn't see yesterday's date when their own
  // clock is past midnight UTC. Once the user has interacted, leave
  // their selection alone.
  const [date, setDate] = useState<string>(() =>
    formatInTimeZone(new Date(), tz, 'yyyy-MM-dd'),
  );
  const [userPickedDate, setUserPickedDate] = useState(false);
  useEffect(() => {
    if (!userPickedDate && account?.timeZone) {
      setDate(formatInTimeZone(new Date(), account.timeZone, 'yyyy-MM-dd'));
    }
  }, [account?.timeZone, userPickedDate]);

  const {
    presses,
    loading: pressesLoading,
    error: pressesError,
    retry: retryPresses,
  } = useButtonPresses(aid ?? null, date, tz);
  const { devices } = useDevices(aid ?? null);

  if (!aid) return null;
  if (accountLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    );
  }

  const elderName = account?.elderName?.trim() || 'this person';
  const isAdmin = role === 'admin';

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <header>
        <h1 className="text-2xl font-semibold text-ink">Activity for {elderName}</h1>
        <p className="mt-1 text-sm text-ink-soft">
          Button presses on a chosen day, plus alerts if there are none.
        </p>
      </header>

      {pressesError && (
        <LoadErrorBanner what="the button presses" onRetry={retryPresses} />
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <CardTitle>Button presses</CardTitle>
            <div className="w-44">
              <Field label="" htmlFor="activity-date">
                <Input
                  id="activity-date"
                  type="date"
                  value={date}
                  onChange={(e) => {
                    setDate(e.target.value);
                    setUserPickedDate(true);
                  }}
                />
              </Field>
            </div>
          </div>
        </CardHeader>
        <CardBody>
          {pressesLoading ? (
            <div className="flex justify-center py-4">
              <Spinner />
            </div>
          ) : (
            <>
              <ActivityTimeline
                presses={presses}
                devices={devices}
                tz={tz}
                wakeHour={account?.wakeHour}
                bedtimeHour={account?.bedtimeHour}
              />
              <ButtonPressList presses={presses} tz={tz} />
            </>
          )}
        </CardBody>
      </Card>

      <AlertsCard aid={aid} isAdmin={isAdmin} />
    </div>
  );
}
